/**
 * AIP Auto-Import Service
 * 
 * Connects to the SSE endpoint to auto-import AIP AD2 data
 * for a given ICAO code. Emits progress steps in real-time.
 */

export interface ImportStep {
    step: number;
    total: number;
    status: 'running' | 'done' | 'error';
    message_es: string;
    message_en: string;
    detail?: string;
}

export interface AIPResult {
    airport_name: string;
    icao_code: string;
    runways: Record<string, unknown>[];
    taxiways: Record<string, unknown>[];
    cns: Record<string, unknown>;
    max_aircraft_pcn: string;
    confidence: number;
    summary: Record<string, unknown>;
    parsed_sections: string[];
}

/**
 * Start the auto-import process for an ICAO code.
 * 
 * @param icaoCode - 4-letter ICAO airport code
 * @param onStep - Called for each progress step
 * @param onComplete - Called when import finishes with parsed AIP data
 * @param onError - Called on failure with an error message
 * @returns A cancel function to abort the import
 */
export function startAutoImport(
    icaoCode: string,
    onStep: (step: ImportStep) => void,
    onComplete: (data: AIPResult) => void,
    onError: (error: string) => void
): () => void {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
    const token = localStorage.getItem('token');

    const abortController = new AbortController();

    const url = `${baseUrl}/aip/auto-import/${icaoCode.toUpperCase()}`;

    // Use fetch with ReadableStream for SSE (more control than EventSource
    // which doesn't support Authorization headers)
    fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
        },
        signal: abortController.signal,
    })
        .then(async (response) => {
            if (!response.ok) {
                const errorBody = await response.text();
                try {
                    const parsed = JSON.parse(errorBody);
                    onError(parsed.detail || `HTTP ${response.status}`);
                } catch {
                    onError(`HTTP ${response.status}: ${errorBody.slice(0, 200)}`);
                }
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) {
                onError('No response body');
                return;
            }

            const decoder = new TextDecoder();
            let buffer = '';
            let lastResult: AIPResult | null = null;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                // Process complete SSE messages (separated by \n\n)
                const parts = buffer.split('\n\n');
                buffer = parts.pop() || ''; // Keep incomplete part

                for (const part of parts) {
                    const dataLine = part
                        .split('\n')
                        .find((line) => line.startsWith('data: '));

                    if (!dataLine) continue;
                    const data = dataLine.slice(6); // Remove "data: "

                    if (data === '[DONE]') {
                        if (lastResult) {
                            onComplete(lastResult);
                        } else {
                            onError('Import completed but no data was received');
                        }
                        return;
                    }

                    try {
                        const step: ImportStep = JSON.parse(data);
                        onStep(step);

                        // If this is the final step with detail, parse the AIP result
                        if (step.detail && step.step === step.total && step.status === 'done') {
                            try {
                                lastResult = JSON.parse(step.detail);
                            } catch {
                                // detail might not be valid JSON
                            }
                        }

                        // If any step is an error, report it
                        if (step.status === 'error') {
                            onError(step.message_es || step.message_en);
                            return;
                        }
                    } catch {
                        // Skip invalid JSON
                    }
                }
            }

            // If we get here without onComplete being called
            if (lastResult) {
                onComplete(lastResult);
            }
        })
        .catch((err) => {
            if (err.name === 'AbortError') {
                // Cancelled by user — don't report as error
                return;
            }
            onError(err.message || 'Connection error');
        });

    // Return cancel function
    return () => {
        abortController.abort();
    };
}
